import React, { useEffect, useRef } from "react";
import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import { api } from "../lib/axios";
import { TransactionDetailsType } from "./Transaction";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";

const pdf = Object.assign({}, pdfMake);
pdf.vfs = Object.assign({}, pdfFonts.vfs);
interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  tax: string;
  amount: number;
}

interface InvoiceData {
  items: InvoiceItem[];
  subtotal: number;
  totalExclTax: string;
  vat: string;
  total: number;
  amountDue: number;
}

interface InvoiceProps {
  isChargingDone: boolean;
  email?: string;
  transactionDetails: TransactionDetailsType;
  pricePerKWh: number;
}

const convertImageToBase64 = async (imageUrl: string): Promise<string> => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error converting image:", error);
    return "";
  }
};

const InvoicePDF: React.FC<InvoiceProps> = ({
  isChargingDone,
  email,
  transactionDetails,
  pricePerKWh,
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ["company-details"],
    queryFn: async () => {
      const { data } = await api.get(`/settings/`);
      return data;
    },
    enabled: isChargingDone,
  });

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isChargingDone && isFirstRender.current && !isLoading) {
      isFirstRender.current = false;
      generatePDF();
    }
  }, [isChargingDone, isLoading]);

  if (!isChargingDone) return;

  const getCurrentDate = (): string => {
    const date = new Date();
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const generatePDF = async (): Promise<void> => {
    if (!transactionDetails) return;
    const logoBase64 = await convertImageToBase64(
      "https://i.imgur.com/73BBBFd.png"
    );

    const parsedStartedDate = parseISO(
      transactionDetails?.startedSession as string
    );

    const formattedStartingDate = format(parsedStartedDate, "yyyy/MM/dd HH:mm");
    const parsedEndingDate = parseISO(
      transactionDetails?.endedSession as string
    );
    const formattedEndingDate = format(parsedEndingDate, "yyyy/MM/dd HH:mm");

    const vat = `1.${data.vat}`;

    try {
      if (transactionDetails.consumptionKWh) {
        const consumptionKWh = parseFloat(transactionDetails.consumptionKWh);

        const baseAmount =
          //@ts-ignore
          Math.round(consumptionKWh * parseFloat(pricePerKWh) * 100) / 100;
        const amountExclTax = baseAmount / +vat;
        const vatAmount = baseAmount - amountExclTax;

        const invoiceData: InvoiceData = {
          items: [
            {
              description: `Session: ${transactionDetails.id} - Start date: ${formattedStartingDate} at charging station ${transactionDetails.chargeBoxId} and ended at ${formattedEndingDate}`,
              quantity: 1,
              unitPrice: 0.0,
              tax: ``,
              amount: 0.0,
            },
            {
              description: `Energy consumption: ${consumptionKWh.toFixed(
                2
                //@ts-ignore
              )} kWh at ${data.currency} ${parseFloat(pricePerKWh).toFixed(
                2
              )}/kWh`,
              quantity: 1,
              unitPrice: baseAmount,
              tax: `${data.vat}%`,
              amount: baseAmount,
            },
          ],
          subtotal: baseAmount,
          totalExclTax: amountExclTax.toFixed(2),
          vat: vatAmount.toFixed(2),
          total: baseAmount,
          amountDue: baseAmount,
        };

        const docDefinition = {
          content: [
            {
              columns: [
                {
                  width: "*",
                  stack: [
                    { text: `${data.name}`, style: "companyName" },
                    { text: `${data.address}` },
                    { text: `Phone: ${data.phoneNumber}` },
                    { text: `Date: ${getCurrentDate()}` },
                  ],
                },
                {
                  width: 100,
                  alignment: "right",
                  image: logoBase64,
                  fit: [100, 100],
                },
              ],
            },
            { text: "\n" },
            {
              columns: [
                {
                  width: "*",
                  stack: [
                    {
                      text: `$${invoiceData.total.toFixed(
                        2
                      )} due ${getCurrentDate()}`,
                      style: "companyName",
                    },
                  ],
                },
              ],
            },
            { text: "\n" },
            {
              layout: "lightHorizontalLines",
              table: {
                headerRows: 1,
                widths: ["*", "auto", "auto", "auto", "auto"],
                body: [
                  [
                    {
                      text: "Description",
                      style: "tableHeader",
                      border: [false, false, false, true],
                      borderColor: "#d1d5db",
                    },
                    {
                      text: "Quantity",
                      style: "tableHeader",
                      border: [false, false, false, true],
                      borderColor: "#d1d5db",
                    },
                    {
                      text: "Unit Price",
                      style: "tableHeader",
                      border: [false, false, false, true],
                      borderColor: "#d1d5db",
                    },
                    {
                      text: "Tax",
                      style: "tableHeader",
                      border: [false, false, false, true],
                      borderColor: "#d1d5db",
                    },
                    {
                      text: "Amount",
                      style: "tableHeader",
                      border: [false, false, false, true],
                      borderColor: "#d1d5db",
                    },
                  ],
                  ...invoiceData.items.map((item) => [
                    {
                      text: item.description,
                      border: [false, false, false, false],
                    },
                    {
                      text: item.quantity.toString(),
                      border: [false, false, false, false],
                    },
                    {
                      text: `$${item.unitPrice.toFixed(2)}`,
                      border: [false, false, false, false],
                    },
                    {
                      text: item.tax,
                      border: [false, false, false, false],
                    },
                    {
                      text: `$${item.amount.toFixed(2)}`,
                      border: [false, false, false, false],
                    },
                  ]),
                ],
              },
            },
            { text: "\n" },
            {
              columns: [
                { width: "*", text: "" },
                {
                  width: "50%",
                  layout: "lightHorizontalLines",
                  table: {
                    widths: ["*", "auto"],
                    body: [
                      [
                        {
                          text: "Subtotal:",
                          border: [false, false, true, true],
                        },
                        {
                          text: `$${invoiceData.subtotal.toFixed(2)}`,
                          alignment: "right",
                          border: [false, false, true, true],
                        },
                      ],
                      [
                        {
                          text: "Total Excluding Tax:",
                          border: [false, false, true, true],
                        },
                        {
                          text: `$${invoiceData.totalExclTax}`,
                          alignment: "right",
                          border: [false, false, true, true],
                        },
                      ],
                      [
                        {
                          text: `VAT (${data.vat}%):`,
                          border: [false, false, true, true],
                        },
                        {
                          text: `$${invoiceData.vat}`,
                          alignment: "right",
                          border: [false, false, true, true],
                        },
                      ],
                      [
                        {
                          text: "Total:",
                          border: [false, false, true, true],
                          style: "tableHeader",
                        },
                        {
                          text: `$${invoiceData.total.toFixed(2)}`,
                          alignment: "right",
                          border: [false, false, true, true],
                          style: "tableHeader",
                        },
                      ],
                      [
                        {
                          text: "Amount Due:",
                          border: [false, false, true, true],
                          style: "tableHeader",
                        },
                        {
                          text: `$${invoiceData.amountDue.toFixed(2)}`,
                          alignment: "right",
                          border: [false, false, true, true],
                          style: "tableHeader",
                        },
                      ],
                    ],
                  },
                },
              ],
            },
          ],
          styles: {
            companyName: {
              fontSize: 14,
              bold: true,
            },
            tableHeader: {
              bold: true,
              color: "#000",
            },
          },
          defaultStyle: {
            fontSize: 10,
            lineHeight: 1.3,
          },
        };

        //@ts-expect-error
        const pdfDocGenerator = pdf.createPdf(docDefinition);

        pdfDocGenerator.getBase64(async (base64Data) => {
          try {
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const pdfBlob = new Blob([byteArray], { type: "application/pdf" });

            const formData = new FormData();
            formData.append("to", `${email}`);
            formData.append(
              "subject",
              `Invoice for the session: ${transactionDetails.id}`
            );
            formData.append("body", import.meta.env.VITE_BODY_EMAIL);
            formData.append("attachment", pdfBlob, "invoice.pdf");

            for (let pair of formData.entries()) {
              console.log(pair[0], typeof pair[1], pair[1]);
            }

            await api.post("/send-email", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
          } catch (err) {
            console.error("Error sending email:", err);
          }
        });
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
    }
  };

  return <div></div>;
};

export default InvoicePDF;
